<script setup lang="ts">
import { computed } from 'vue'
import { TAB_BUTTON_POSITION } from '@/utils/tabs'
const props = defineProps({
  apiResponse: {
    type: String,
    required: true,
    default: ''
  },
  tabPosition: {
    type: String,
    required: false
  },
  errorResponse: {
    type: String,
    required: true,
    default: ''
  }
})
const isTabMaximized = computed(() => {
  return props.tabPosition === TAB_BUTTON_POSITION.MAXIMIZE
})
/**
 * Checks whether a given string is valid JSON.
 * @param data - The data to check if it's a valid JSON string.
 * @returns - Returns `true` if the data is valid JSON, otherwise `false`.
 */
const isJson = (data: any) => {
  try {
    JSON.parse(data)
    return true
  } catch (error) {
    return false
  }
}
/**
 * Formats a JSON string with indentation for better readability.
 * @param data - The JSON string to format.
 * @returns The formatted JSON string, or an error message if parsing fails.
 */
const formatJson = (data: string) => {
  try {
    const jsonData = JSON.parse(data)
    return JSON.stringify(jsonData, null, 2)
  } catch (error: any) {
    return data
  }
}
</script>

<template>
  <div
    class="h-full w-full max-w-full flex-col"
    :class="[
      {
        'sm:max-w-[calc(100vw-110px)] md:max-w-[calc(100vw-110px)] lg:max-w-[calc(100vw-128px)]':
          !isTabMaximized
      }
    ]"
  >
    <div v-if="props.apiResponse">
      <h3 class="text-lg font-medium">Response Data:</h3>
      <pre class="mt-2 overflow-x-auto rounded-lg border border-gray-300 p-4">
        <code>{{ formatJson(apiResponse) }}</code>
      </pre>
    </div>

    <!-- Display Error Response -->
    <div v-if="props.errorResponse">
      <pre class="overflow-y-auto rounded-lg border border-gray-300 p-2">
        <code v-if="isJson(errorResponse)">
        {{ formatJson(errorResponse) }}
      </code>
      <div v-else v-html="errorResponse"></div>
      </pre>
    </div>
  </div>
</template>
<style scoped>
pre {
  white-space: pre-wrap;
  word-wrap: break-word;
}
</style>
